import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Button, Input, Modal, Radio, Select, Space, Table, Tag, Tooltip, Upload } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { UploadFile } from 'antd/es/upload/interface'
import { CloudUploadOutlined, InboxOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { useToast } from '../contexts/ToastContext'
import { useAnalyzeSchemaMutation, useClientTableNames, useCreateFileRecordMutation, useUploadHistory } from '../hooks/useFileUpload'
import type { RequiredDateIssue, RowRemovalIssue, SchemaColumn, SchemaMismatch, UploadHistoryRow, UploadMode } from '../types/fileUpload'
import { useAuthSession } from '../lib/session'
import { ApiRequestError } from '../lib/httpClient'
import { useCan } from '../contexts/AuthorizationContext'
import { permissions, UserTypes } from '../types/authorization'

const { Dragger } = Upload

function renderSchemaValue(value?: string | number) {
  if (value == null || value === '') return '—'
  return value
}

function extractColumnName(message: string) {
  const match = message.match(/column '([^']+)'/i)
  return match?.[1] ?? '—'
}

function formatCount(count: number) {
  return `${count} ${count === 1 ? 'row' : 'rows'}`
}

function formatInvalidRequiredDateMessage(issues: RequiredDateIssue[]) {
  return issues
    .filter((issue) => issue.invalidRowCount > 0)
    .map((issue) => `${formatCount(issue.invalidRowCount)} contain values that are not valid dates in the "${issue.displayName}" column`)
    .join(' and ')
}

function formatRowRemovalMessage(issues: RowRemovalIssue[]) {
  return issues
    .filter((issue) => issue.removedRowCount > 0)
    .map((issue) => {
      const count = issue.removedRowCount
      return `${count} ${count === 1 ? 'row has' : 'rows have'} empty values in the "${issue.displayName}" column`
    })
    .join(' and ')
}

function getStatusTag(status: string) {
  const normalized = status.toLowerCase()
  if (normalized.includes('completed') || normalized.includes('success') || normalized.includes('uploaded')) {
    return <Tag color="green">Completed</Tag>
  }
  if (normalized.includes('failed') || normalized.includes('error')) return <Tag color="red">Failed</Tag>
  if (normalized.includes('processing') || normalized.includes('progress')) return <Tag color="cyan">Processing</Tag>
  if (normalized.includes('pending') || normalized.includes('queued') || normalized.includes('new')) {
    return <Tag color="gold">Pending</Tag>
  }
  // Show the raw status rather than mislabeling anything unrecognized as "Failed".
  return <Tag>{status || 'Unknown'}</Tag>
}

export default function FileUpload() {
  const session = useAuthSession()
  const { addToast } = useToast()
  const canManageRules = useCan(permissions.viewAdminUser)
  const isClientUploadUser = session?.currentUserTypeId === UserTypes.ClientAdmin || session?.currentUserTypeId === UserTypes.ClientUser

  const [file, setFile] = useState<File | null>(null)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [existingTableName, setExistingTableName] = useState<string | undefined>(undefined)
  const [newTableName, setNewTableName] = useState('')
  const [uploadMode, setUploadMode] = useState<UploadMode>('Replace')
  const [schemaModalOpen, setSchemaModalOpen] = useState(false)
  const [schemaColumns, setSchemaColumns] = useState<SchemaColumn[]>([])
  const [schemaExistingColumns, setSchemaExistingColumns] = useState<SchemaColumn[]>([])
  const [schemaErrors, setSchemaErrors] = useState<string[]>([])
  const [schemaRuleViolations, setSchemaRuleViolations] = useState<string[]>([])
  const [schemaMismatches, setSchemaMismatches] = useState<SchemaMismatch[]>([])
  const [requiredDateIssues, setRequiredDateIssues] = useState<RequiredDateIssue[]>([])
  const [rowRemovalIssues, setRowRemovalIssues] = useState<RowRemovalIssue[]>([])
  const [originalRowCount, setOriginalRowCount] = useState(0)
  const [retainedRowCount, setRetainedRowCount] = useState(0)
  const [removedRowCount, setRemovedRowCount] = useState(0)
  const [historyNameFilter, setHistoryNameFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const selectedTableName = (isClientUploadUser ? existingTableName : (newTableName || existingTableName || ''))?.trim() || ''
  const effectiveUploadMode: UploadMode = isClientUploadUser ? 'Replace' : uploadMode
  const clientId = session?.currentClientId

  const historyQuery = useUploadHistory({ clientId, page: currentPage, pageSize })
  const tablesQuery = useClientTableNames(clientId)
  const analyzeSchemaMutation = useAnalyzeSchemaMutation()
  const createFileRecordMutation = useCreateFileRecordMutation()

  const rows = useMemo(() => historyQuery.data?.items || [], [historyQuery.data?.items])
  const totalItems = historyQuery.data?.totalItems || rows.length
  const filteredRows = useMemo(() => {
    const q = historyNameFilter.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((row) => row.fileName.toLowerCase().includes(q) || row.tableName.toLowerCase().includes(q))
  }, [historyNameFilter, rows])

  const canAnalyze = !!file && !!selectedTableName
  const allErrorMessages = [...schemaErrors, ...schemaRuleViolations]
  const hasNullErrors = allErrorMessages.length > 0
  const nullErrorRows = allErrorMessages.map((message, index) => ({
    key: `error-${index}`,
    column: extractColumnName(message),
    message,
  }))
  const schemaComparison = useMemo(
    () =>
      schemaColumns
        .map((col) => {
          const existing = schemaExistingColumns.find((e) => e.name === col.name)
          return { key: col.name, name: col.name, newType: col.type, existingType: existing?.type ?? '' }
        })
        .filter((row) => row.existingType !== '' && row.newType !== row.existingType),
    [schemaColumns, schemaExistingColumns],
  )
  const hasMismatch = schemaComparison.length > 0 || schemaMismatches.length > 0
  const hasInvalidRequiredDateValues = requiredDateIssues.some((issue) => issue.invalidRowCount > 0)
  const hasRowsToRemove = rowRemovalIssues.some((issue) => issue.removedRowCount > 0)
  const isUploadBlocked = hasInvalidRequiredDateValues || hasNullErrors || (!isClientUploadUser && uploadMode === 'Append' && hasMismatch)

  const resetSchemaFeedback = () => {
    setSchemaExistingColumns([])
    setSchemaErrors([])
    setSchemaRuleViolations([])
    setSchemaMismatches([])
    setRequiredDateIssues([])
    setRowRemovalIssues([])
    setOriginalRowCount(0)
    setRetainedRowCount(0)
    setRemovedRowCount(0)
  }

  const analyzeSchemaAction = async () => {
    if (!file || !selectedTableName) {
      addToast('Select a file and table name first.', 'warning')
      return
    }
    try {
      resetSchemaFeedback()
      const result = await analyzeSchemaMutation.mutateAsync({ file, tableName: selectedTableName, uploadMode: effectiveUploadMode, clientId })
      setSchemaColumns(result.columns)
      setSchemaExistingColumns(result.existingTableColumns)
      setSchemaErrors(result.errors)
      setSchemaRuleViolations(result.ruleViolations)
      setSchemaMismatches(result.schemaMismatches)
      setRequiredDateIssues(result.requiredDateIssues)
      setRowRemovalIssues(result.rowRemovalIssues)
      setOriginalRowCount(result.originalRowCount ?? result.totalRows ?? 0)
      setRetainedRowCount(result.retainedRowCount ?? result.totalRows ?? 0)
      setRemovedRowCount(result.removedRowCount ?? 0)
      if (isClientUploadUser && !result.rowRemovalIssues.some((issue) => issue.removedRowCount > 0) && !result.requiredDateIssues.some((issue) => issue.invalidRowCount > 0)) {
        await uploadAction()
        return
      }
      setSchemaModalOpen(true)
    } catch (e) {
      if (e instanceof ApiRequestError) {
        const payload = (e.payload || {}) as { mismatches?: SchemaMismatch[]; requiredDateIssues?: RequiredDateIssue[]; rowRemovalIssues?: RowRemovalIssue[] }
        if (e.code === 'NO_RETAINED_ROWS') {
          setSchemaErrors([e.message])
          setSchemaModalOpen(true)
          return
        }
        if (payload.mismatches?.length) {
          setSchemaMismatches(payload.mismatches)
          setSchemaModalOpen(true)
          return
        }
        if (payload.requiredDateIssues?.length) {
          setRequiredDateIssues(payload.requiredDateIssues)
          if (!isClientUploadUser) {
            setSchemaErrors([formatInvalidRequiredDateMessage(payload.requiredDateIssues)])
          }
          setSchemaModalOpen(true)
          return
        }
        if (payload.rowRemovalIssues?.length) {
          setRowRemovalIssues(payload.rowRemovalIssues)
          setSchemaModalOpen(true)
          return
        }
      }
      addToast(e instanceof Error ? e.message : 'Schema analysis failed', 'error')
    }
  }

  const uploadAction = async () => {
    if (!file || !selectedTableName) {
      addToast('File and table name are required.', 'warning')
      return
    }
    try {
      resetSchemaFeedback()
      await createFileRecordMutation.mutateAsync({
        file,
        tableName: selectedTableName,
        uploadMode: effectiveUploadMode,
        clientId,
      })
      addToast('File uploaded successfully.', 'success')
      setFile(null)
      setFileList([])
      setNewTableName('')
      setExistingTableName(undefined)
      setSchemaColumns([])
      setRequiredDateIssues([])
      setRowRemovalIssues([])
      setSchemaModalOpen(false)
      setCurrentPage(1)
    } catch (e) {
      if (e instanceof ApiRequestError) {
        const payload = (e.payload || {}) as { mismatches?: SchemaMismatch[]; requiredDateIssues?: RequiredDateIssue[]; rowRemovalIssues?: RowRemovalIssue[] }
        if (e.code === 'NO_RETAINED_ROWS') {
          setSchemaErrors([e.message])
          setSchemaModalOpen(true)
          return
        }
        if (payload.mismatches?.length) {
          setSchemaMismatches(payload.mismatches)
          setSchemaModalOpen(true)
          return
        }
        if (payload.requiredDateIssues?.length) {
          setRequiredDateIssues(payload.requiredDateIssues)
          if (!isClientUploadUser) {
            setSchemaErrors([formatInvalidRequiredDateMessage(payload.requiredDateIssues)])
          }
          setSchemaModalOpen(true)
          return
        }
        if (payload.rowRemovalIssues?.length) {
          setRowRemovalIssues(payload.rowRemovalIssues)
          setSchemaModalOpen(true)
          return
        }
      }
      addToast(e instanceof Error ? e.message : 'File upload failed', 'error')
    }
  }

  const columns: ColumnsType<UploadHistoryRow> = [
    { title: 'File ID', dataIndex: 'fileId', key: 'fileId' },
    { title: 'File', dataIndex: 'fileName', key: 'fileName' },
    { title: 'Table', dataIndex: 'tableName', key: 'tableName' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (value: string) => getStatusTag(value) },
    { title: 'Rows', dataIndex: 'rowCount', key: 'rowCount' },
    { title: 'Updated', dataIndex: 'uploadedAt', key: 'uploadedAt' },
  ]

  const schemaReviewColumns: ColumnsType<SchemaColumn> = [
    { title: 'Column', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'rawDataType', key: 'rawDataType', render: (value: string, row) => renderSchemaValue(value || row.type) },
    { title: 'Sample Value', dataIndex: 'sampleValue', key: 'sampleValue', render: renderSchemaValue },
    { title: 'Length', dataIndex: 'maxLength', key: 'maxLength', render: renderSchemaValue },
    { title: 'Precision', dataIndex: 'precision', key: 'precision', render: renderSchemaValue },
    { title: 'Scale', dataIndex: 'scale', key: 'scale', render: renderSchemaValue },
  ]

  const mismatchTable =
    schemaComparison.length > 0 ? (
      <Table<{ key: string; name: string; newType: string; existingType: string }>
        rowKey="key"
        pagination={false}
        scroll={{ y: 240 }}
        dataSource={schemaComparison}
        columns={[
          { title: 'Column', dataIndex: 'name', key: 'name' },
          { title: 'New Type', dataIndex: 'newType', key: 'newType' },
          { title: 'Existing Type', dataIndex: 'existingType', key: 'existingType' },
        ]}
      />
    ) : (
      <Table<SchemaMismatch>
        rowKey={(row) => `${row.column}-${row.message}`}
        pagination={false}
        scroll={{ y: 240 }}
        dataSource={schemaMismatches}
        columns={[
          { title: 'Column', dataIndex: 'column', key: 'column' },
          { title: 'Mismatch', dataIndex: 'message', key: 'message' },
        ]}
      />
    )

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white via-white to-amber-50/35">
      <div className="px-6 pt-5 pb-4 border-b border-brand-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-gray-900 tracking-tight">File Upload</h1>
            <p className="text-sm text-gray-500 mt-1">Upload new files and monitor upload history</p>
          </div>
          <Tag color="gold-inverse" className="px-3 py-1 rounded-full font-medium">
            {totalItems} history rows
          </Tag>
        </div>
      </div>

      <div className="p-4 bg-white/70">
        <Space wrap size={12} className="w-full">
          {!isClientUploadUser && (
            <Radio.Group
              value={uploadMode}
              onChange={(e) => setUploadMode(e.target.value as UploadMode)}
              options={[
                { value: 'Replace', label: 'Replace' },
                { value: 'Append', label: 'Append' },
              ]}
            />
          )}
          <Select
            value={existingTableName}
            onChange={setExistingTableName}
            loading={tablesQuery.isLoading}
            allowClear
            showSearch
            placeholder="Select existing table"
            options={(tablesQuery.data || []).map((t) => ({ value: t, label: t }))}
            filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
            optionFilterProp="label"
            style={{ width: 240 }}
          />
          {!isClientUploadUser && (
            <Input
              value={newTableName}
              onChange={(e) => setNewTableName(e.target.value)}
              placeholder="Or add new table name"
              style={{ width: 240 }}
            />
          )}
          <Tooltip title="Refresh history">
            <Button
              icon={<ReloadOutlined />}
              aria-label="Refresh history"
              onClick={() => void historyQuery.refetch()}
              loading={historyQuery.isFetching}
            />
          </Tooltip>
          <Tooltip title="Upload file">
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              aria-label="Upload file"
              onClick={() => void analyzeSchemaAction()}
              disabled={!canAnalyze}
              loading={analyzeSchemaMutation.isPending}
            >
              Upload
            </Button>
          </Tooltip>
          {canManageRules && (
            <Tooltip title="Manage upload validation rules">
              <Link to={existingTableName ? `/fileupload/rules?table=${encodeURIComponent(existingTableName)}` : '/fileupload/rules'}>
                <Button icon={<SafetyCertificateOutlined />}>Rules</Button>
              </Link>
            </Tooltip>
          )}
        </Space>

        <div className="mt-4">
          <Dragger
            multiple={false}
            showUploadList
            fileList={fileList}
            beforeUpload={(nextFile) => {
              setFile(nextFile)
              setFileList([nextFile])
              setSchemaColumns([])
              resetSchemaFeedback()
              return false
            }}
            onRemove={() => {
              setFile(null)
              setFileList([])
              setSchemaColumns([])
              resetSchemaFeedback()
            }}
            accept=".csv,.xlsx"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined className="text-brand-500" />
            </p>
            <p className="ant-upload-text">Drop file here or click to select</p>
            <p className="ant-upload-hint">Accepted: CSV, XLSX</p>
          </Dragger>
        </div>
      </div>

      <div className="p-4">
        <Space wrap size={12} className="mb-3">
          <Input
            placeholder="Search file or table"
            value={historyNameFilter}
            onChange={(e) => setHistoryNameFilter(e.target.value)}
            allowClear
            style={{ width: 260 }}
          />
        </Space>

        {historyQuery.isError && (
          <Alert
            type="error"
            showIcon
            className="mb-3"
            message={(historyQuery.error as Error)?.message || 'Failed to load upload history'}
          />
        )}

        <Table<UploadHistoryRow>
          rowKey="id"
          loading={historyQuery.isLoading}
          columns={columns}
          dataSource={filteredRows}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalItems,
            showSizeChanger: false,
            showTotal: (total, range) => `${range[0] ?? 0} - ${range[1] ?? 0} of ${total}`,
          }}
          onChange={(pagination: TablePaginationConfig) => {
            const nextPage = pagination.current || 1
            setCurrentPage(nextPage)
          }}
        />
      </div>

      <Modal
        open={schemaModalOpen}
        title={isClientUploadUser ? 'Upload validation' : 'Schema review'}
        width={960}
        okText={isClientUploadUser ? 'Upload' : 'Confirm and Upload'}
        onOk={() => void uploadAction()}
        onCancel={() => {
          setSchemaModalOpen(false)
        }}
        okButtonProps={{ loading: createFileRecordMutation.isPending, disabled: isUploadBlocked }}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
          },
        }}
      >
        {isClientUploadUser ? (
          hasNullErrors ? (
            <Alert
              type="error"
              showIcon
              message="Upload cannot continue"
              description={allErrorMessages.join(' ')}
            />
          ) : hasInvalidRequiredDateValues ? (
            <Alert
              type="error"
              showIcon
              message="Upload cannot continue"
              description={formatInvalidRequiredDateMessage(requiredDateIssues)}
            />
          ) : hasRowsToRemove ? (
            <Alert
              type="warning"
              showIcon
              message={`${formatRowRemovalMessage(rowRemovalIssues)}. Clicking "Upload" will remove these rows and proceed with the upload. Do you want to continue?`}
              description={`${removedRowCount} of ${originalRowCount} rows will be removed; ${retainedRowCount} rows will be uploaded.`}
            />
          ) : (
            <Alert type="success" showIcon message="No configured null rows need to be removed. The upload is ready to continue." />
          )
        ) : hasNullErrors ? (
          <>
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message="Schema analysis returned errors"
              description="Resolve the issues below before uploading."
            />
            <Table<{ key: string; column: string; message: string }>
              rowKey="key"
              pagination={false}
              dataSource={nullErrorRows}
              columns={[
                { title: 'Column', dataIndex: 'column', key: 'column', width: 220 },
                { title: 'Error', dataIndex: 'message', key: 'message' },
              ]}
            />
          </>
        ) : uploadMode === 'Append' && hasMismatch ? (
          <>
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message="Schema mismatch with existing table"
              description="The selected file cannot be appended because some existing columns do not match."
            />
            {mismatchTable}
          </>
        ) : uploadMode === 'Replace' && hasMismatch ? (
          <>
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message="Column type changes detected"
              description="Existing table column types will be updated by this replace upload. Review the changes below before continuing."
            />
            {mismatchTable}
            <Table<SchemaColumn>
              rowKey="name"
              className="mt-4"
              pagination={false}
              dataSource={schemaColumns}
              columns={schemaReviewColumns}
            />
          </>
        ) : (
          <Table<SchemaColumn>
            rowKey="name"
            pagination={false}
            dataSource={schemaColumns}
            columns={schemaReviewColumns}
          />
        )}
      </Modal>
    </div>
  )
}
